export interface Address {
  name: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
}

export interface PackageData {
  id: string;
  packageNumber: string;
  slotCode: string;
  barcodes: string[];
  packageMetadata?: {
    sender?: Address;
    receiver?: Address;
    weight?: { value: number; unit: string };
    carrier: string;
    serviceType?: string;
    timestamp?: string;
    dimensions?: { width: number; height: number; length: number; unit: string };
    trackingNumbers?: string[];
    referenceNumber?: string;
    customNote?: string | null;
  };
  imageUrl?: string;
  frameNumber?: string | null;
  storageOption?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  boxNumber?: string | null;
  name?: string;
}

export const STORAGE_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  CENTER: { bg: 'bg-white', border: 'border-purple-700', text: 'text-forground' },
  LARGE: { bg: 'bg-white', border: 'border-yellow-700', text: 'text-forground' },
  SAFE: { bg: 'bg-white', border: 'border-teal-700', text: 'text-forground' },
};

export const storageStyle = (option?: string | null) =>
  STORAGE_STYLES[option ?? ''] ?? { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-500' };

const CARRIER_FALLBACK_PALETTE = [
  { bg: 'bg-rose-100', border: 'border-rose-300', text: 'text-rose-800' },
  { bg: 'bg-cyan-100', border: 'border-cyan-300', text: 'text-cyan-800' },
  { bg: 'bg-lime-100', border: 'border-lime-300', text: 'text-lime-800' },
  { bg: 'bg-fuchsia-100', border: 'border-fuchsia-300', text: 'text-fuchsia-800' },
  { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800' },
  { bg: 'bg-teal-100', border: 'border-teal-300', text: 'text-teal-800' },
  { bg: 'bg-sky-100', border: 'border-sky-300', text: 'text-sky-800' },
  { bg: 'bg-violet-100', border: 'border-violet-300', text: 'text-violet-800' },
];

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

export const carrierStyle = (carrier?: string) => {
  switch (carrier?.toLowerCase()) {
    case 'dpd':
      return { bg: 'bg-emerald-100', border: 'border-emerald-300', text: 'text-emerald-800' };
    case 'dhl':
      return { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800' };
    case 'gls':
      return { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-800' };
    case 'hermes':
      return { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-800' };
    case 'deutsche post':
      return { bg: 'bg-indigo-100', border: 'border-indigo-300', text: 'text-indigo-800' };
    case 'other':
      return { bg: 'bg-slate-200', border: 'border-slate-400', text: 'text-slate-800' };
    case undefined:
    case '':
      return { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700' };
    default:
      return CARRIER_FALLBACK_PALETTE[hashString(carrier.toLowerCase()) % CARRIER_FALLBACK_PALETTE.length];
  }
};

export const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
